import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  addDoc,
  collection,
  query,
  where,
  getDocs,
  deleteDoc,
  writeBatch,
  orderBy,     // ✅ Added for sorting queries
  limit,       // ✅ Added for limiting query results
  startAfter   // ✅ Added for pagination (future use)
} from 'firebase/firestore';
import { db, auth } from '../firebase';
import { UserProfile, Quest, DailyActivity, Program } from '../types';

// Helper function to recursively remove undefined values from objects
const cleanObject = (obj: any): any => {
  if (obj === null || obj === undefined) {
    return null;
  }
  
  if (Array.isArray(obj)) {
    return obj.map(item => cleanObject(item));
  }
  
  if (typeof obj === 'object') {
    const cleaned: any = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        cleaned[key] = cleanObject(value);
      }
    }
    return cleaned;
  }
  
  return obj;
};

export const firestoreService = {
  // User Profile Operations
  async getUserProfile(userId: string): Promise<UserProfile | null> {
    try {
      const docRef = doc(db, 'users', userId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data() as UserProfile;
        
        // Backward compatibility: Add penalty fields if they don't exist
        if (data.penaltyActive === undefined) {
          data.penaltyActive = false;
        }
        if (data.penaltyQuestIds === undefined) {
          data.penaltyQuestIds = [];
        }
        // Backward compatibility: Add lastLoginDate if it doesn't exist
        if (data.lastLoginDate === undefined) {
          data.lastLoginDate = '';
        }
        // Backward compatibility: Add lastPenaltyCheck if it doesn't exist
        if (data.lastPenaltyCheck === undefined) {
          data.lastPenaltyCheck = '';
        }
        // Backward compatibility: Add passiveStatBoosts if it doesn't exist
        if (data.passiveStatBoosts === undefined) {
          data.passiveStatBoosts = {
            strength: 0,
            intelligence: 0,
            agility: 0,
            vitality: 0,
            sense: 0,
          };
        }
        // Backward compatibility: Add unallocatedStatPoints if it doesn't exist
        if (data.unallocatedStatPoints === undefined) {
          data.unallocatedStatPoints = 0;
        }
        // Backward compatibility: Ensure inventory is always an array
        if (!Array.isArray(data.inventory)) {
          data.inventory = [];
        }
        // Backward compatibility: Add lifetimeStats if it doesn't exist
        if (data.lifetimeStats === undefined) {
          data.lifetimeStats = {
            totalQuestsCompleted: 0,
            totalXPEarned: 0,
            totalCurrencyEarned: 0,
            questsByDifficulty: {
              easy: 0,
              normal: 0,
              hard: 0,
              boss: 0,
            },
            questsByType: {
              daily: 0,
              main: 0,
              instant: 0,
              boss: 0,
            },
          };
        }
        // Backward compatibility: Add image/video collections if they don't exist
        if (data.backgroundSettings) {
          if (!data.backgroundSettings.imageCollection) {
            data.backgroundSettings.imageCollection = [];
          }
          if (!data.backgroundSettings.videoCollection) {
            data.backgroundSettings.videoCollection = [];
          }
        }
        
        return data;
      }
      return null;
    } catch (error) {
      console.error('Error getting user profile:', error);
      throw error;
    }
  },

  async saveUserProfile(userId: string, profile: UserProfile): Promise<void> {
    try {
      const docRef = doc(db, 'users', userId);
      // Recursively remove undefined fields (Firestore doesn't allow them)
      const cleanProfile = cleanObject(profile);
      // 🔒 SECURITY: Strip protected fields that client must not modify
      const { subscription, aiUsage, lemonSqueezy, ...profileWithoutProtected } = (cleanProfile as any);
      // 🔒 SECURITY: Use merge to preserve server-managed fields
      await setDoc(docRef, profileWithoutProtected, { merge: true });
    } catch (error) {
      console.error('Error saving user profile:', error);
      throw error;
    }
  },

  async updateUserProfile(userId: string, updates: Partial<UserProfile>): Promise<void> {
    try {
      const docRef = doc(db, 'users', userId);
      // Recursively remove undefined fields (Firestore doesn't allow them)
      const cleanUpdates = cleanObject(updates) as any;
      // 🔒 SECURITY: Strip protected fields that client must not modify
      const { subscription, aiUsage, lemonSqueezy, ...updatesWithoutProtected } = cleanUpdates || {};
      await updateDoc(docRef, updatesWithoutProtected as any);
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  },

  // Quest Operations
  /**
   * Get user quests with optimization options
   * ✅ OPTIMIZED: Added query limits to prevent loading all quests
   * @param userId - User ID
   * @param options - Query options for filtering and limiting results
   * @returns Array of quests
   */
  async getUserQuests(userId: string, options?: {
    includeCompleted?: boolean;
    limit?: number;
    daysAgo?: number;
  }): Promise<Quest[]> {
    const {
      includeCompleted = false,
      limit: queryLimit = 200,
      daysAgo = 30
    } = options || {};
    
    try {
      let q;
      
      if (includeCompleted) {
        // Get all quests for user (both active and completed)
        // Note: We filter by date client-side to avoid needing a composite index
        q = query(
          collection(db, 'quests'),
          where('userId', '==', userId),
          limit(queryLimit)
        );
      } else {
        // Get only active quests (most common case)
        q = query(
          collection(db, 'quests'),
          where('userId', '==', userId),
          where('status', '==', 'active'),
          limit(queryLimit)
        );
      }
      
      const querySnapshot = await getDocs(q);
      
      let quests = querySnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      } as Quest));
      
      // If includeCompleted, filter by date client-side
      if (includeCompleted && daysAgo) {
        const cutoffDate = Date.now() - (daysAgo * 24 * 60 * 60 * 1000);
        quests = quests.filter(quest => quest.createdAt > cutoffDate);
      }
      
      // Sort by createdAt descending
      quests.sort((a, b) => b.createdAt - a.createdAt);
      
      return quests;
    } catch (error) {
      console.error('Error getting user quests:', error);
      throw error;
    }
  },

  async saveQuest(userId: string, quest: Quest): Promise<void> {
    try {
      const docRef = doc(db, 'quests', quest.id);
      // Recursively remove undefined fields (Firestore doesn't allow them)
      const cleanQuest = cleanObject({ ...quest, userId });
      await setDoc(docRef, cleanQuest);
    } catch (error) {
      console.error('Error saving quest:', error);
      throw error;
    }
  },

  async saveQuests(userId: string, quests: Quest[]): Promise<void> {
    try {
      const batch = writeBatch(db);
      
      quests.forEach(quest => {
        const docRef = doc(db, 'quests', quest.id);
        // Recursively remove undefined fields (Firestore doesn't allow them)
        const cleanQuest = cleanObject({ ...quest, userId });
        batch.set(docRef, cleanQuest);
      });
      
      await batch.commit();
    } catch (error) {
      console.error('Error saving quests:', error);
      throw error;
    }
  },

  async deleteQuest(questId: string): Promise<void> {
    try {
      const docRef = doc(db, 'quests', questId);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting quest:', error);
      throw error;
    }
  },

  /**
   * Delete multiple quests in a single batch operation (more efficient)
   * Firestore allows up to 500 operations per batch
   */
  async deleteBulkQuests(questIds: string[]): Promise<void> {
    try {
      if (questIds.length === 0) return;
      
      // Split into batches of 500 (Firestore limit)
      const batchSize = 500;
      for (let i = 0; i < questIds.length; i += batchSize) {
        const batch = writeBatch(db);
        const batchIds = questIds.slice(i, i + batchSize);
        
        for (const questId of batchIds) {
          const docRef = doc(db, 'quests', questId);
          batch.delete(docRef);
        }
        
        await batch.commit();
      }
    } catch (error) {
      console.error('Error deleting quests in batch:', error);
      throw error;
    }
  },

  // Statistics Operations
  /**
   * Get user statistics with date range limit
   * ✅ OPTIMIZED: Only load recent statistics (prevents unlimited growth)
   * @param userId - User ID
   * @param options - Query options for date filtering
   * @returns Array of daily activities
   */
  async getUserStatistics(userId: string, options?: {
    daysAgo?: number;
    limit?: number;
  }): Promise<DailyActivity[]> {
    const {
      daysAgo = 90,  // Default to last 90 days
      limit: queryLimit = 90
    } = options || {};
    
    try {
      // Calculate start date
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - daysAgo);
      const startDateStr = startDate.toISOString().split('T')[0];
      
      const q = query(
        collection(db, 'statistics'),
        where('userId', '==', userId),
        where('date', '>=', startDateStr),
        orderBy('date', 'desc'),
        limit(queryLimit)
      );
      
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        ...doc.data()
      } as DailyActivity));
    } catch (error) {
      console.error('Error getting user statistics:', error);
      throw error;
    }
  },

  async saveStatistics(userId: string, statistics: DailyActivity[]): Promise<void> {
    try {
      const batch = writeBatch(db);
      
      statistics.forEach(stat => {
        const docRef = doc(db, 'statistics', `${userId}_${stat.date}`);
        batch.set(docRef, {
          ...stat,
          userId
        });
      });
      
      await batch.commit();
    } catch (error) {
      console.error('Error saving statistics:', error);
      throw error;
    }
  },

  async saveDailyStatistic(userId: string, statistic: DailyActivity): Promise<void> {
    try {
      const docRef = doc(db, 'statistics', `${userId}_${statistic.date}`);
      await setDoc(docRef, {
        ...statistic,
        userId
      });
    } catch (error) {
      console.error('Error saving daily statistic:', error);
      throw error;
    }
  },

  // Program Operations
  async getUserPrograms(userId: string): Promise<Program[]> {
    try {
      const q = query(
        collection(db, 'programs'),
        where('userId', '==', userId)
      );
      
      const querySnapshot = await getDocs(q);
      const programs: Program[] = [];
      
      querySnapshot.forEach((doc) => {
        programs.push(doc.data() as Program);
      });
      
      return programs;
    } catch (error) {
      console.error('Error getting user programs:', error);
      return [];
    }
  },

  async saveProgram(userId: string, program: Program): Promise<void> {
    try {
      const docRef = doc(db, 'programs', program.id);
      // Recursively remove undefined fields (Firestore doesn't allow them)
      const cleanProgram = cleanObject({ ...program, userId });
      await setDoc(docRef, cleanProgram);
    } catch (error) {
      console.error('Error saving program:', error);
      throw error;
    }
  },

  async savePrograms(userId: string, programs: Program[]): Promise<void> {
    try {
      const batch = writeBatch(db);
      
      programs.forEach(program => {
        const docRef = doc(db, 'programs', program.id);
        // Recursively remove undefined fields (Firestore doesn't allow them)
        const cleanProgram = cleanObject({ ...program, userId });
        batch.set(docRef, cleanProgram);
      });
      
      await batch.commit();
    } catch (error) {
      console.error('Error saving programs:', error);
      throw error;
    }
  },

  async updateProgram(programId: string, updates: Partial<Program>): Promise<void> {
    try {
      const docRef = doc(db, 'programs', programId);
      // Recursively remove undefined fields (Firestore doesn't allow them)
      const cleanUpdates = cleanObject(updates);
      await updateDoc(docRef, cleanUpdates);
    } catch (error) {
      console.error('Error updating program:', error);
      throw error;
    }
  },

  async deleteProgram(programId: string): Promise<void> {
    try {
      await deleteDoc(doc(db, 'programs', programId));
    } catch (error) {
      console.error('Error deleting program:', error);
      throw error;
    }
  },

  // 🛡️ FAIL-SAFE: Distributed locking for phase generation
  async acquireGenerationLock(programId: string, phase: number, userId: string): Promise<boolean> {
    try {
      const lockId = `${programId}-phase-${phase}`;
      const lockRef = doc(db, 'generationLocks', lockId);
      const lockDoc = await getDoc(lockRef);
      
      // Check if lock exists and is still valid (< 15 minutes old)
      if (lockDoc.exists()) {
        const lockData = lockDoc.data();
        const lockAge = Date.now() - lockData.acquiredAt;
        const MAX_LOCK_AGE = 15 * 60 * 1000; // 15 minutes
        
        if (lockAge < MAX_LOCK_AGE) {
          console.log(`🔒 Lock for ${lockId} is held by another process (age: ${Math.floor(lockAge / 1000)}s)`);
          return false;
        }
        
        // Lock is stale, we can override it
        console.log(`⏰ Lock for ${lockId} is stale (age: ${Math.floor(lockAge / 60000)} minutes), overriding...`);
      }
      
      // Acquire the lock
      await setDoc(lockRef, {
        programId,
        phase,
        userId,
        acquiredAt: Date.now()
      });
      
      console.log(`✅ Lock acquired for ${lockId}`);
      return true;
    } catch (error) {
      console.error('Error acquiring generation lock:', error);
      return false;
    }
  },

  async releaseGenerationLock(programId: string, phase: number): Promise<void> {
    try {
      const lockId = `${programId}-phase-${phase}`;
      const lockRef = doc(db, 'generationLocks', lockId);
      await deleteDoc(lockRef);
      console.log(`🔓 Lock released for ${lockId}`);
    } catch (error) {
      console.error('Error releasing generation lock:', error);
      // Don't throw - lock will expire naturally
    }
  },

  // Sync localStorage data to Firestore (one-time migration)
  async syncLocalDataToFirestore(userId: string): Promise<void> {
    try {
      // Get data from localStorage
      const localProfile = localStorage.getItem('user_profile');
      const localQuests = localStorage.getItem('quests');
      const localStats = localStorage.getItem('daily_statistics');

      if (localProfile) {
        const profile = JSON.parse(localProfile);
        await this.saveUserProfile(userId, profile);
      }

      if (localQuests) {
        const quests = JSON.parse(localQuests);
        await this.saveQuests(userId, quests);
      }

      if (localStats) {
        const stats = JSON.parse(localStats);
        await this.saveStatistics(userId, stats);
      }
    } catch (error) {
      console.error('Error syncing local data to Firestore:', error);
      throw error;
    }
  },

  // Passive Skills Operations
  async getUserPassiveSkills(userId: string): Promise<any[]> {
    try {
      const q = query(
        collection(db, 'passive_skills'),
        where('userId', '==', userId)
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting passive skills:', error);
      throw error;
    }
  },

  async savePassiveSkill(userId: string, skill: any): Promise<string> {
    try {
      const cleanedSkill = cleanObject({ ...skill, userId });
      const docRef = await addDoc(collection(db, 'passive_skills'), cleanedSkill);
      return docRef.id;
    } catch (error) {
      console.error('Error saving passive skill:', error);
      throw error;
    }
  },

  async updatePassiveSkill(skillId: string, updates: any): Promise<void> {
    try {
      const cleanedUpdates = cleanObject(updates);
      const docRef = doc(db, 'passive_skills', skillId);
      await updateDoc(docRef, cleanedUpdates);
    } catch (error) {
      console.error('Error updating passive skill:', error);
      throw error;
    }
  },

  async deletePassiveSkill(skillId: string): Promise<void> {
    try {
      await deleteDoc(doc(db, 'passive_skills', skillId));
    } catch (error) {
      console.error('Error deleting passive skill:', error);
      throw error;
    }
  },

  // ============= ACHIEVEMENTS =============
  async saveAchievements(userId: string, achievements: any[]): Promise<void> {
    try {
      const docRef = doc(db, 'achievements', userId);
      await setDoc(docRef, { achievements, userId }, { merge: true });
    } catch (error) {
      console.error('Error saving achievements:', error);
      throw error;
    }
  },

  async getAchievements(userId: string): Promise<any[] | null> {
    try {
      const docRef = doc(db, 'achievements', userId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return docSnap.data().achievements || [];
      }
      return null;
    } catch (error) {
      console.error('Error getting achievements:', error);
      throw error;
    }
  }
};



